import React, { forwardRef, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  InputAdornment,
  MenuItem,
  Snackbar,
  TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import AddBoxIcon from "@mui/icons-material/AddBox";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import CheckIcon from "@mui/icons-material/Check";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ClearIcon from "@mui/icons-material/Clear";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import LastPageIcon from "@mui/icons-material/LastPage";
import RemoveIcon from "@mui/icons-material/Remove";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import SearchIcon from "@mui/icons-material/Search";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import MaterialTable from "@material-table/core";

import { isSuperAdminFromToken, isAdminFromToken, getUserIdFromToken } from "../../utils/jwtUtils";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";

const Settings = ({ handleUser, openUserModal, setOpenUserModal }) => {
  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const axiosPrivate = useAxiosPrivate();
  const [roleId, setRoleId] = useState("");
  const [roles, setRoles] = useState([]);
  const currentUserId = getUserIdFromToken();

  useEffect(() => {
    async function fetchRoles() {
      try {
        const res = await axiosPrivate.get("/api/roles/v1");
        setRoles(res.data);
      } catch (err) {
        console.error("fetchRoles error", err);
      }
    }

    fetchRoles();
  }, []);

  console.log("roles", roles);

  useEffect(() => {
    setIsSuperAdmin(isSuperAdminFromToken());
    setIsAdmin(isAdminFromToken());
  }, []);

  const tableIcons = {
    Add: forwardRef((props, ref) => <AddBoxIcon {...props} ref={ref} />),
    Check: forwardRef((props, ref) => <CheckIcon {...props} ref={ref} />),
    Clear: forwardRef((props, ref) => <ClearIcon {...props} ref={ref} />),
    Delete: forwardRef((props, ref) => (
      <DeleteOutlineIcon {...props} ref={ref} />
    )),
    DetailPanel: forwardRef((props, ref) => (
      <ChevronRightIcon {...props} ref={ref} />
    )),
    Edit: forwardRef((props, ref) => <EditIcon {...props} ref={ref} />),
    Export: forwardRef((props, ref) => <SaveAltIcon {...props} ref={ref} />),
    Filter: forwardRef((props, ref) => <FilterAltIcon {...props} ref={ref} />),
    FirstPage: forwardRef((props, ref) => (
      <FirstPageIcon {...props} ref={ref} />
    )),
    LastPage: forwardRef((props, ref) => <LastPageIcon {...props} ref={ref} />),
    NextPage: forwardRef((props, ref) => (
      <ChevronRightIcon {...props} ref={ref} />
    )),
    PreviousPage: forwardRef((props, ref) => (
      <ChevronLeftIcon {...props} ref={ref} />
    )),
    ResetSearch: forwardRef((props, ref) => <ClearIcon {...props} ref={ref} />),
    Search: forwardRef((props, ref) => <SearchIcon {...props} ref={ref} />),
    SortArrow: forwardRef((props, ref) => (
      <ArrowDownwardIcon {...props} ref={ref} />
    )),
    ThirdStateCheck: forwardRef((props, ref) => (
      <RemoveIcon {...props} ref={ref} />
    )),
    ViewColumn: forwardRef((props, ref) => (
      <ViewColumnIcon {...props} ref={ref} />
    )),
  };

  const columns = [
    { title: "User ID", field: "user_id", filtering: false, sorting: true },
    { title: "Email", field: "email", filtering: true, sorting: true },
    { title: "Username", field: "username", filtering: false, sorting: true },
    {
      title: "Role",
      field: "role",
      filtering: true,
      sorting: true,
      render: (rowData) => (
        <p style={{ textTransform: "capitalize" }}>{rowData.role}</p>
      ),
    },
    {
      title: "Is Superadmin",
      field: "is_superadmin",
      filtering: false,
      sorting: true,
      render: (rowData) => (rowData.is_superadmin ? "Yes" : "No"),
    },
    {
      title: "Auth Source",
      field: "auth_provider",
      filtering: true,
      sorting: true,
      render: (rowData) => (
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <span
            style={{
              padding: "4px 10px",
              borderRadius: "12px",
              fontSize: "12px",
              fontWeight: 600,
              textTransform: "uppercase",
              backgroundColor:
                rowData.auth_provider === "azure"
                  ? "#00a4ef20"
                  : rowData.auth_provider === "google"
                  ? "#ea433520"
                  : "#49758d20",
              color:
                rowData.auth_provider === "azure"
                  ? "#00a4ef"
                  : rowData.auth_provider === "google"
                  ? "#ea4335"
                  : "#49758d",
            }}
          >
            {rowData.auth_provider === "azure" ? "Microsoft AD" : rowData.auth_provider}
          </span>
        </div>
      ),
    },
    // {
    //   title: "Created Date",
    //   field: "created_date",
    //   filtering: false,
    //   sorting: true,
    //   render: (rowData) =>
    //     rowData.created_date
    //       ? new Date(rowData.created_date).toLocaleString()
    //       : "",
    // },
    // {
    //   title: "Updated Date",
    //   field: "updated_date",
    //   filtering: false,
    //   sorting: true,
    //   render: (rowData) =>
    //     rowData.updated_date
    //       ? new Date(rowData.updated_date).toLocaleString()
    //       : "",
    // },
    {
      title: "Actions",
      hidden: !isAdmin,
      render: (rowData) =>
        isAdmin ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "25px",
            }}
          >
            <IconButton
              onClick={() => {
                setEmail(rowData.email);
                setUsername(rowData.username);
                setPassword("");
                setSelectedUserId(rowData.user_id);
                setRoleId(rowData.role_id);
                setIsEditMode(true);
                setOpenUserModal(true);
              }}
            >
              <EditIcon />
            </IconButton>

            <IconButton
              disabled={rowData.user_id === parseInt(currentUserId)}
              onClick={() => {
                setUserToDelete(rowData.user_id);
                setDeleteDialogOpen(true);
              }}
              style={{
                opacity: rowData.user_id === parseInt(currentUserId) ? 0.3 : 1,
              }}
            >
              <DeleteOutlineIcon />
            </IconButton>
          </div>
        ) : null,
      sorting: false,
      filtering: false,
    },
  ];

  async function fetchUsers() {
    setLoading(true);
    try {
      const res = await axiosPrivate.get("/api/users");
      const usersResp = Array.isArray(res.data)
        ? res.data
        : res.data.users || [];
      setUsers(
        usersResp.map((u) => ({
          user_id: u.user_id,
          email: u.email,
          username: u.username,
          role: u.role,
          role_id: u.role_id,
          is_superadmin: !!u.is_superadmin,
          auth_provider: u.auth_provider || "local",
          created_date: u.created_date,
          updated_date: u.updated_date,
        })),
      );
    } catch (err) {
      console.error("fetchUsers error", err);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }

  const handleCloseUserModal = () => {
    setOpenUserModal(false);
    setIsEditMode(false);
    setSelectedUserId(null);
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    if (openUserModal && !isEditMode) {
      setEmail("");
      setUsername("");
      setPassword("");
      setRoleId("");
    }
  }, [openUserModal, isEditMode]);

  async function createUser(finalEmail, finalUsername, finalPassword) {
    try {
      const res = await axiosPrivate.post("/api/users", {
        email: finalEmail,
        username: finalUsername,
        password: finalPassword,
        role_id: roleId,
      });

      setSnackbar({
        open: true,
        message: "User created successfully",
        severity: "success",
      });

      await fetchUsers();

      return res.data;
    } catch (err) {
      console.error("Create user error:", err);
      setSnackbar({
        open: true,
        message: err.response?.data?.message || "Failed to create user",
        severity: "error",
      });
    }
  }

  const handleDeleteUser = async () => {
    if (!userToDelete) return;

    try {
      setIsDeleting(true);

      await axiosPrivate.delete(`/api/users/${userToDelete}`);

      setSnackbar({
        open: true,
        message: "User deleted successfully",
        severity: "success",
      });

      await fetchUsers();
    } catch (err) {
      console.error("Delete user error:", err);

      setSnackbar({
        open: true,
        message: "Failed to delete user",
        severity: "error",
      });
    } finally {
      setIsDeleting(false);
      setDeleteDialogOpen(false);
      setUserToDelete(null);
    }
  };

  const sendUserData = async (e) => {
    e.preventDefault();

    let finalUsername = username?.trim();
    let finalEmail = email?.trim();

    if (!finalUsername && finalEmail.includes("@")) {
      finalUsername = finalEmail.split("@")[0];
      setUsername(finalUsername);
    }
    if (isEditMode) {
      try {
        await axiosPrivate.put(`/api/users/${selectedUserId}`, {
          email: finalEmail,
          username: finalUsername,
          ...(password && { password }),
          role_id: roleId,
        });

        await fetchUsers();

        setSnackbar({
          open: true,
          message: "User updated successfully",
          severity: "success",
        });
      } catch (err) {
        console.error("Update error", err);

        setSnackbar({
          open: true,
          message: "Failed to update user",
          severity: "error",
        });
      }
    } else {
      await createUser(finalEmail, finalUsername, password);
    }

    handleCloseUserModal();
    setEmail("");
    setUsername("");
    setPassword("");
    setIsEditMode(false);
    setSelectedUserId(null);
  };

  return (
    <>
      {isAdmin && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
          <Button
            variant="contained"
            onClick={handleUser}
            sx={{
              fontSize: "12px",
              borderRadius: "10px",
              backgroundColor: "#49758d",
              color: "#fff",
              border: "1px solid #49758d",
              fontFamily: "Work Sans",
              textTransform: "none",
              padding: "8px 24px",
              "&:hover": {
                backgroundColor: "#3f667a",
                border: "1px solid #3f667a",
              },
            }}
          >
            Add Users
          </Button>
        </Box>
      )}
      <div>
        <MaterialTable
          title=""
          icons={tableIcons}
          columns={columns}
          data={users}
          isLoading={loading}
          options={{
            search: false,
            paging: true,
            toolbar: false,
            pageSize: 10,
            exportButton: true,
            filtering: false,
            padding: "dense",
            headerStyle: {
              fontWeight: 700,
              color: "#316C7A",
              fontSize: "14px",
              height: "3.5rem",
              fontFamily: "Work Sans",
              backgroundColor: "transparent",
              borderBottom: "2px solid #EEE",
            },
            rowStyle: {
              fontSize: "14px",
              color: "#14285A",
              fontFamily: "Work Sans",
              borderBottom: "2px solid #EEE",
            },
          }}
        />
      </div>
      <Dialog
        open={openUserModal}
        onClose={(event, reason) => {
          if (reason !== "backdropClick") {
            handleCloseUserModal();
          }
        }}
        maxWidth="md"
        BackdropProps={{
          style: {
            boxShadow: "none",
            backdropFilter: "blur(0.4px)",
          },
        }}
        PaperProps={{
          style: {
            overflow: "hidden",
            boxShadow: "rgba(0, 0, 0, 0.1) 0px 1px 2px",
          },
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <h3
            onClose={handleCloseUserModal}
            style={{ padding: "34px 24px 24px 34px" }}
          >
            User Details
          </h3>

          <IconButton
            edge="end"
            color="inherit"
            onClick={handleCloseUserModal}
            aria-label="close"
            style={{
              width: "24px",
              display: "flex",
              float: "right",
              padding: "25px",
              marginRight: "12px",
              marginTop: "16px",
            }}
          >
            <CloseIcon />
          </IconButton>
        </div>
        <DialogContent style={{ padding: "20px", minWidth: "500px" }}>
          <form
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "20px",
              marginTop: "10px",
            }}
            onSubmit={sendUserData}
          >
            <TextField
              className="field"
              fullWidth
              id="email"
              label="Email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              variant="outlined"
              margin="normal"
              slotProps={{
                inputLabel: { shrink: true },
              }}
            />

            <TextField
              className="field"
              fullWidth
              id="password"
              label="Password"
              placeholder="Enter your password"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              variant="outlined"
              margin="normal"
              slotProps={{
                inputLabel: { shrink: true },
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        onMouseDown={(e) => e.preventDefault()}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />
            <TextField
              select
              label="Role"
              value={roleId}
              onChange={(e) => setRoleId(e.target.value)}
              style={{ textTransform: "capitalize" }}
              fullWidth
            >
              {roles.map((role) => (
                <MenuItem
                  key={role.role_id}
                  value={role.role_id}
                  style={{ textTransform: "capitalize" }}
                >
                  {role.role_title}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Username"
              className="field"
              id="username"
              placeholder="Enter your username"
              fullWidth
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              variant="outlined"
              margin="normal"
              slotProps={{
                inputLabel: { shrink: true },
              }}
            />

            <Button
              variant="contained"
              type="submit"
              style={{
                marginTop: "10px",
                borderRadius: "10px",
                backgroundColor: "#49758d",
                color: "#fff",
                border: "1px solid #49758d",
                fontFamily: "Work Sans",
              }}
            >
              {isEditMode ? "Update User Details" : "Submit"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={2000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogContent>
          <h3>Delete User</h3>
          <p>Are you sure you want to delete this user?</p>

          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "10px",
              marginTop: "20px",
            }}
          >
            <Button
              variant="outlined"
              onClick={() => setDeleteDialogOpen(false)}
              style={{ textTransform: "none" }}
            >
              Cancel
            </Button>

            <Button
              variant="contained"
              color="error"
              onClick={handleDeleteUser}
              disabled={isDeleting}
              style={{ textTransform: "none" }}
            >
              {isDeleting ? "Deleting..." : "Delete"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default Settings;
